/**
 * Helpers for creating and managing notifications.
 * Notifications are stored in Firestore collection "notifications".
 */

import { notificationsRef, serverTimestamp, usersRef } from './collections';
import { getProductRef } from './collections';

/**
 * Create a notification for a user.
 * @param {string} userId - Recipient user id
 * @param {string} type - offer | message | price_drop | review | system | admin_moderation
 * @param {string} title - Short title
 * @param {string} body - Notification body text
 * @param {object} data - Optional payload (productId, offerId, conversationId, actionUrl, etc.)
 */
export function createNotification(userId, type, title, body, data = {}) {
  if (!userId) return Promise.resolve();
  return notificationsRef().add({
    userId,
    type: type || 'system',
    title: title || '',
    body: body || '',
    data: data || {},
    actionUrl: data?.actionUrl || null,
    read: false,
    createdAt: serverTimestamp(),
  });
}

/**
 * Send notification to all admin users.
 * @param {string} type - Notification type
 * @param {string} title - Notification title
 * @param {string} body - Notification body
 * @param {object} data - Notification data payload
 */
export async function notifyAdmins(type, title, body, data = {}) {
  try {
    const snap = await usersRef().where('role', '==', 'admin').get();
    if (snap.empty) return [];
    const promises = snap.docs.map((doc) =>
      createNotification(doc.id, type, title, body, data)
    );
    return Promise.all(promises);
  } catch (err) {
    console.error('Failed to notify admins:', err);
    return [];
  }
}

/**
 * Resolves the destination URL for a notification item when clicked.
 * @param {object} notif - The notification object
 * @param {string} currentUserId - The current logged in user ID
 * @returns {string} - The URL path to navigate to
 */
export function getNotificationTarget(notif, currentUserId) {
  if (!notif) return '/notifications';
  if (notif.actionUrl) return notif.actionUrl;

  const data = notif.data || {};
  const type = notif.type || '';

  // 1. Chat Message
  if (type === 'message' || data.conversationId) {
    if (data.conversationId) {
      return `/chat/${data.conversationId}`;
    }
    return '/messages';
  }

  // 2. Offer received / updated
  if (type === 'offer') {
    if (data.conversationId) return `/chat/${data.conversationId}`;
    if (data.offerId) return '/dashboard/offers';
    if (data.productId) return `/ad/${data.productId}`;
    return '/dashboard/offers';
  }

  // 3. Reviews
  if (type === 'review') {
    if (data.sellerId) return `/profile/${data.sellerId}`;
    if (data.productId) return `/ad/${data.productId}`;
    return currentUserId ? `/profile/${currentUserId}` : '/profile';
  }

  // 4. Followers
  if (type === 'follow' || type === 'follower') {
    if (data.followerId) return `/profile/${data.followerId}`;
    return currentUserId
      ? `/profile/${currentUserId}/followers`
      : '/notifications';
  }

  // 5. Verifications / ID
  if (type === 'verification' || type === 'id_verification') {
    return '/profile/edit';
  }

  // 6. Price drop / Ad update
  if (type === 'price_drop' || data.productId) {
    if (data.productId) return `/ad/${data.productId}`;
  }

  // 7. General fallback
  return '/notifications';
}

/**
 * Notify users who have this product in watchlist with alertPrice >= newPrice.
 * Call after updating product price. product doc should have watchers: { [userId]: alertPrice }.
 */
export function notifyPriceDrop(productId, newPrice, productName) {
  const numPrice = Number(newPrice);
  if (!productId || isNaN(numPrice)) return Promise.resolve();
  return getProductRef(productId)
    .get()
    .then((doc) => {
      if (!doc.exists) return;
      const watchers = doc.data().watchers || {};
      const promises = [];
      Object.keys(watchers).forEach((userId) => {
        const alertPrice = Number(watchers[userId]);
        if (!isNaN(alertPrice) && numPrice <= alertPrice) {
          promises.push(
            createNotification(
              userId,
              'price_drop',
              'Alerta de preço alcançado!',
              `"${productName || 'O anúncio que você observa'}" agora está R$ ${numPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (seu valor alvo era R$ ${alertPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`,
              { productId }
            )
          );
        }
      });
      return Promise.all(promises);
    });
}
